import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Text } from '@zillow/constellation';
import { Col, Container, Row } from '@zillow/zrs-comet';

import HeadWrapper from 'app/shared/components/HeadWrapper';
import InfiniteScroller from 'app/shared/components/InfiniteScroller';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import SelectPropertyConstants from 'app/shared/constants/SelectPropertyConstants';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// selectors
import {
    RoutingSelectors,
} from 'app/shared/selectors';

// actions
import PropertiesActions from 'app/shared/actions/PropertiesActions';

// models
import ListingInt from 'app/shared/model/ListingsInt';

import { RMWContext } from 'app/shared/providers/RMWContext';

// utils
import queryUtils from 'app/shared/utils/queryUtils';
import SelectPropertyCard from './SelectPropertyCard';
import {
    StyledCol,
    StyledSpacer,
    StyledDivContainer,
    StyledCard,
    StyledSelectPropertyCardContent,
    StyledSelectPropertyPageTitle,
} from './StyledComponents';

const propTypes = {
    search: PropTypes.string.isRequired,
};

const SelectPropertyPage = ({
    dispatch,
    search,
    // unit testing hack
    utIsLoading = true,
}) => {
    const { paths } = React.useContext(RMWContext);
    const [isLoading, setIsLoading] = useState(utIsLoading);
    const [isDataFetching, setIsDataFetching] = useState(true);
    const [disableLoadMore] = useState(false);
    const [listingIntModel, setListingIntModel] = useState(ListingInt({}));
    const [type] = useState(queryUtils.parse(search).type);
    const [propertyList, setPropertyList] = useState([]);

    const PAGINATION_LIMIT = 20;

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        setIsLoading(false);

        /* eslint-disable no-use-before-define */
        fetchListings().then(({ listingIntModel, propertyList }) => {
            updateState({ listingIntModel, propertyList });
        }).catch((errorRMWModel) => {
            console.error('debug - errorRMWModel', errorRMWModel);
            setIsDataFetching(false);
        });
    }, []);
    /* eslint-enable react-hooks/exhaustive-deps */

    const fetchListings = (startKeyExclusive) => {
        setIsDataFetching(true);

        let newListingIntModel = ListingInt({});
        let newPropertyList = [];

        return new Promise((resolve) => {
            dispatch(PropertiesActions.getIntListings(type, {
                limit: PAGINATION_LIMIT,
                startKeyExclusive,
            })).then((response) => {
                newListingIntModel = ListingInt(response);
                newPropertyList = [...propertyList, ...newListingIntModel.listings];

                resolve({
                    listingIntModel: newListingIntModel,
                    propertyList: newPropertyList,
                });
            }).finally(() => {
                setIsDataFetching(false);
            });
        });
    };

    const updateState = ({ listingIntModel, propertyList }) => {
        setListingIntModel(listingIntModel);
        setPropertyList(propertyList);
    };

    const handleLoadMoreClick = () => {
        const { endKey } = listingIntModel.pager;

        fetchListings(endKey).then(({ listingIntModel, propertyList }) => {
            updateState({ listingIntModel, propertyList });
        });
    };

    const renderPropertyList = () => {
        const propertyPath = `${paths.properties}?postingPath=true`;

        const propertyCardList = propertyList.map((listing, i) => (
            <StyledCol
                key={i}
                padding={0}
                marginBottom="sm"
                xs={12}
                xl={4}
            >
                <SelectPropertyCard
                    type={type}
                    listing={listing}
                />
            </StyledCol>
        ));

        const StyledAddNewText = styled(Text)`
            text-align: center;
        `;

        // append add new property card
        const addNewPropertyItem = (
            <StyledCol
                key="add-new-property"
                padding={0}
                marginBottom="sm"
                xs={12}
                xl={4}
            >
                <Link to={propertyPath}>
                    <StyledCard
                        mediaQuery={{ xl: { marginRight: 'md' } }}
                        cardType="secondary"
                    >
                        <StyledSelectPropertyCardContent padding={2.5}>
                            <StyledAddNewText fontType="body" as="div">Add a new property</StyledAddNewText>
                        </StyledSelectPropertyCardContent>
                    </StyledCard>
                </Link>
            </StyledCol>
        );

        propertyCardList.push(addNewPropertyItem);

        return (
            <React.Fragment>
                <Row>
                    {propertyCardList}
                </Row>
            </React.Fragment>
        );
    };

    const renderLoadMoreBtn = () => {
        const { pager } = listingIntModel;

        return (
            <StyledSpacer
                marginX={-1}
                paddingY="xs"
            >
                <InfiniteScroller
                    pager={pager}
                    text="Load more properties"
                    onLoadMoreClick={handleLoadMoreClick}
                    disabled={disableLoadMore}
                />
            </StyledSpacer>
        );
    };

    const renderTitle = () => {
        let typeDisplay = '';

        switch (type) {
            case SelectPropertyConstants.LISTINGS:
                typeDisplay = 'the listing';
                break;

            // fall through
            default:
                typeDisplay = type;
                break;
        }

        const title = `Select the property you would like to manage ${typeDisplay} for.`;

        return (
            <Row>
                <Col>
                    <StyledSelectPropertyPageTitle
                        marginY="lg"
                        mediaQuery={{
                            xxl: {
                                marginTop: 10, marginBottom: 12,
                            },
                        }}
                        fontType="h6"
                        as="div"
                        className="SelectPropertyPage-Title"
                    >
                        {title}
                    </StyledSelectPropertyPageTitle>
                </Col>
            </Row>
        );
    };

    const renderLoadComplete = () => (
        <Container>
            {renderTitle()}
            {renderPropertyList()}
            {isDataFetching && <SpinnerContainer />}
            {renderLoadMoreBtn()}
        </Container>
    );

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <div className="SelectPropertyPage">
                <StyledDivContainer>
                    <HeadWrapper title="Select Property" />

                    {isLoading ?
                        <SpinnerContainer /> :
                        renderLoadComplete()
                    }
                </StyledDivContainer>
            </div>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    search: RoutingSelectors.makeSearch(state),
});

SelectPropertyPage.propTypes = propTypes;

export { SelectPropertyPage };
export default connect(mapStateToProps)(SelectPropertyPage);
